import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Collapse, Paper, Typography } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import PanelBlock, { PanelEyebrow } from '../PanelBlock';
import ConsistencySection from './ConsistencySection';
import SectionAssessment from './SectionAssessment';
import { getLaboklinApi } from '../../../services/laboklinApi';
import { LABOKLIN_BORDER, LABOKLIN_RED } from '../../../theme/laboklinTheme';
import type { Assessment, AssessmentSection, ReportDetail } from '../../../types/labConsistency';

/**
 * One report, five runs, three graded sections.
 *
 * The verdict comes first, because it decides whether the sections below it mean
 * anything: a contaminated sample must produce no diagnoses, so an empty differential
 * is either the right answer or a defect, and only the verdict says which.
 */

export const ConsistencyReportView: React.FC = () => {
  const { batchId, reportNo } = useParams<{ batchId: string; reportNo: string }>();
  const navigate = useNavigate();
  const [detail, setDetail] = useState<ReportDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showWhy, setShowWhy] = useState(false);

  useEffect(() => {
    if (!batchId || !reportNo) return;
    let live = true;
    getLaboklinApi()
      .getConsistencyReport(batchId, reportNo)
      .then((d) => live && setDetail(d))
      .catch((e) => live && setError(e instanceof Error ? e.message : String(e)));
    return () => {
      live = false;
    };
  }, [batchId, reportNo]);

  if (error) return <Alert severity="error" sx={{ m: 3 }}>{error}</Alert>;
  if (!detail) return <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress size={24} /></Box>;

  const { report, runs, step } = detail;
  const byId = new Map<AssessmentSection, Assessment>();
  for (const a of detail.assessments) byId.set(a.section, a);

  const o = report.outcome;
  const split = o.agreement < 1;
  const gotDifferential = !split && o.majority === 'differential';

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', p: { xs: 2, md: 3 } }}>
      <Button size="small" onClick={() => navigate(`/mylab2/consistency-reports/${batchId}`)} sx={{ textTransform: 'none', mb: 1.5 }}>
        ← All reports
      </Button>
      <Typography sx={{ fontSize: 20, fontWeight: 600 }}>{report.reportNo}</Typography>
      <Typography sx={{ fontSize: 13, color: 'text.secondary', mb: 2 }}>
        {o.runs} identical runs · {step === 'basic' ? 'clinical history withheld' : 'as MyLab shows it'}
        {o.abnormalCount !== null && ` · ${o.abnormalCount} of ${o.analyteCount} values out of range`}
      </Typography>

      <Paper elevation={0} sx={{ border: `1px solid ${LABOKLIN_BORDER}`, borderRadius: 1 }}>
        {/*
          What you would have been shown, before how much it varied.

          A split is the serious case: the same report answered two different ways, both
          reaching a veterinarian as fact. Everything else is stated plainly and NOT
          marked as a fault - "no differential" is the right answer on a panel with
          nothing out of range, and which it is here is a clinical judgement this page
          asks for rather than makes.
        */}
        <PanelBlock>
          <PanelEyebrow label="What you would have seen" state={`${o.runs} runs`} />
          {split ? (
            <>
              <Typography sx={{ fontSize: 14, fontWeight: 600, color: LABOKLIN_RED, mb: 0.75 }}>
                The runs did not agree on what to show you.
              </Typography>
              <Typography sx={{ fontSize: 13 }}>
                {[
                  o.differential && `${o.differential} produced a differential`,
                  o.noDifferential && `${o.noDifferential} produced none`,
                  o.notDiagnostic && `${o.notDiagnostic} judged the sample not diagnostic`,
                ]
                  .filter(Boolean)
                  .join(', ')}
                {' — from the same report.'}
              </Typography>
            </>
          ) : (
            <Typography sx={{ fontSize: 14 }}>
              {o.majority === 'differential' && 'Every run produced a differential.'}
              {/*
                Anchored to this report's own numbers, never a general reassurance.
                Saying "on a panel with nothing out of range that is the right answer"
                on a report with two of two values raised argues the wrong way on the
                one case where it matters most - and the reader has no reason to check.
              */}
              {o.majority === 'noDifferential' &&
                (o.abnormalCount === 0
                  ? 'No run produced a differential. Nothing on this panel was out of range, so that may well be the right answer — your call.'
                  : o.abnormalCount === null
                    ? 'No run produced a differential. Whether there should have been one is your call.'
                    : `No run produced a differential, although ${o.abnormalCount} of ${o.analyteCount} values on this panel are out of range. Whether one should have been produced is your call.`)}
              {o.majority === 'notDiagnostic' &&
                'Every run judged this sample not diagnostic and produced no differential, which is required for a contaminated or impossible sample.'}
            </Typography>
          )}
          {o.reasons.map((reason) => (
            <Typography key={reason} sx={{ fontSize: 13, color: 'text.secondary', mt: 0.75, pl: 1.25, borderLeft: `2px solid ${LABOKLIN_BORDER}` }}>
              {reason}
            </Typography>
          ))}
        </PanelBlock>

        {/*
          Nothing to grade unless a differential was actually produced. Showing three
          empty sections scored 1.00 invited reading a report that answered nothing as
          the best in the batch - which is exactly what happened.
        */}
        {!gotDifferential ? (
          <PanelBlock>
            <PanelEyebrow label="Diagnoses and exclusions" state="Nothing to compare" />
            <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
              {o.majority === 'notDiagnostic'
                ? 'No differential or exclusions were produced, and none should have been.'
                : 'There is no differential to compare between runs. Whether there should have been one is the question below.'}
            </Typography>
            <SectionAssessment
              batchId={report.batchId}
              reportNo={report.reportNo}
              step={step}
              section="overall"
              contentHash={report.contentHash}
              existing={byId.get('overall')}
            />
          </PanelBlock>
        ) : (
          <>
            <ConsistencySection
              section="differentials"
              stability={report.differentials}
              engine={report.engineDifferentials}
              runs={runs}
              report={report}
              step={step}
              assessment={byId.get('differentials')}
              emptyMessage="No differential was produced in any run."
              proseFor={(run, id) =>
                run.differentials.find((d) => d.diagnosisId === id)?.summary ?? null
              }
              rankFor={(run, id) => run.differentials.find((d) => d.diagnosisId === id)?.rank ?? null}
            />

            <ConsistencySection
              section="setAside"
              stability={report.setAside}
              engine={report.engineSetAside}
              runs={runs}
              report={report}
              step={step}
              assessment={byId.get('setAside')}
              emptyMessage="Nothing was set aside in any run — which is a valid answer when nothing is contradicted."
              proseFor={(run, id) => {
                const entry = run.setAside.find((d) => d.diagnosisId === id);
                if (!entry) return null;
                return [entry.reason, entry.whatWouldSettleIt].filter(Boolean).join(' ');
              }}
            />
          </>
        )}

        {/*
          Follow-up labs are graded whatever the outcome, unlike the two sections above.
          They do not depend on there being a differential: a report with no diagnosis
          can still warrant a test - 2608R99114 offers a Toxoplasma PCR in every run,
          which is the most useful thing on that page - and a contaminated sample needs
          the repeat. Folding this into "nothing to compare" hid a section that on one
          report suggested five completely disjoint sets of tests across five runs.
        */}
        <>
            <ConsistencySection
              section="tests"
              stability={report.tests}
              runs={runs}
              report={report}
              step={step}
              assessment={byId.get('tests')}
              emptyMessage="No follow-up tests were offered in any run."
              proseFor={(run, id) => {
                const t = run.tests.find((x) => x.serviceNo === id);
                if (!t) return null;
                return [t.settles, t.price !== null ? `EUR ${t.price.toFixed(2)}` : null]
                  .filter(Boolean)
                  .join(' · ');
              }}
            />

            {/*
              Next steps carry no ids, so they cannot be set-compared. Saying so beats
              implying a precision the measurement does not have.
            */}
            <PanelBlock>
              <PanelEyebrow label="What to do next" state="Presence and count only" />
              <Typography sx={{ fontSize: 12, color: 'text.secondary', mb: 1 }}>
                These are free prose with no identifiers, so they are not scored for agreement.
              </Typography>
              {runs.map((run) => (
                <Box key={run.run} sx={{ mb: 1 }}>
                  <Typography sx={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'text.secondary', fontWeight: 600 }}>
                    Run {run.run} · {run.nextSteps.length} step{run.nextSteps.length === 1 ? '' : 's'}
                  </Typography>
                  {run.nextSteps.map((s, i) => (
                    <Typography key={i} sx={{ fontSize: 13, lineHeight: 1.55 }}>• {s}</Typography>
                  ))}
                </Box>
              ))}
            </PanelBlock>
        </>

        {/*
          Off by default. A veterinarian is grading three panels, and an extracted
          symptom list is machinery rather than an answer - but it settles the one
          question the panel numbers cannot, which is what to blame when the ENGINE's
          own ranking is what moved.
        */}
        <PanelBlock>
          <Button size="small" onClick={() => setShowWhy(!showWhy)} sx={{ textTransform: 'none', fontSize: 12 }}>
            {showWhy ? 'Hide' : 'Why did this vary?'}
          </Button>
          {showWhy && (
            <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 0.75 }}>
              The working behind each answer. Not part of your assessment — it is here so that
              when the diagnoses move between runs, we can see whether the report was read
              differently or only written up differently.
            </Typography>
          )}
          <Collapse in={showWhy} unmountOnExit>
            <Box sx={{ mt: 1 }}>
              {runs.map((run) => (
                <Box key={run.run} sx={{ mb: 1.25 }}>
                  <Typography sx={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'text.secondary', fontWeight: 600 }}>
                    Run {run.run}
                  </Typography>
                  <Typography sx={{ fontSize: 12 }}>
                    <strong>Findings the system extracted from the report:</strong>{' '}
                    {run.symptoms.map((s) => s.name).join(', ') || '(none)'}
                  </Typography>
                  <Typography sx={{ fontSize: 12 }}>
                    <strong>Diagnoses GekkoVet&rsquo;s knowledge base returned for them:</strong>{' '}
                    {run.engineDiagnoses.map((d) => d.name).join(', ') || '(none)'}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Collapse>
        </PanelBlock>
      </Paper>
    </Box>
  );
};

export default ConsistencyReportView;
